import {
  BehaviorSubject,
  combineLatest,
  EMPTY,
  iif,
  Observable,
  of,
  Subject,
} from 'rxjs';
import { LocalStorageKey } from 'src/app/core/constants/local-storage-keys';
import {
  IntentProperties,
  IntentValue,
  UserIntentState,
} from '../../interfaces/intent.interface';
// eslint-disable-next-line @typescript-eslint/naming-convention
import * as Sentry from '@sentry/angular';
import { Injectable, Injector } from '@angular/core';
import {
  debounceTime,
  distinctUntilChanged,
  filter,
  first,
  map,
  mergeMap,
  switchMap,
  takeWhile,
  tap,
} from 'rxjs/operators';
import { OnboardingIntentStep } from '../../enums/intent.enum';
import { IntentApiService } from './intent-api.service';
import { Auth0Service } from '@geneplanet/ngx-auth0';
import { environment } from 'src/environments/environment';
import { NavigationStart, Router, Event } from '@angular/router';
import { IntentStrategyService } from './intent-strategy.service';
import { extractAccount } from '../../operators/extract-account';
import { Auth0Account } from 'src/app/accounts/interfaces/accounts.types';

@Injectable({ providedIn: 'root' })
export class IntentService {
  public state$ = new BehaviorSubject<UserIntentState | undefined>(undefined);
  public inIntent = false;

  private debounceIllegalSateCall = new Subject<void>();

  constructor(
    private intentApiService: IntentApiService,
    private intentStrategyService: IntentStrategyService,
    private auth0Service: Auth0Service,
    private injector: Injector,
    private router: Router
  ) {
    this.cancelLegacyIntents();

    // This is to make sure API is debounced even if it's called many times
    this.debounceIllegalSateCall.pipe(debounceTime(10)).subscribe(() => {
      this.fetchUserIntent().subscribe();
    });
  }

  public startIntent(intentId: string) {
    return this.intentApiService.createUserIntent(intentId).pipe(
      tap(({ id }) => {
        this.setUserIntentIdToLocalStorage(id);
        this.setState({ userIntentId: id });
      })
    );
  }

  public fetchUserIntent(): Observable<UserIntentState | unknown> {
    const userIntentId = this.getIntentIdFromLocalStorage();

    if (userIntentId) {
      return this.intentApiService
        .getCurrentStateByUserIntentId(userIntentId)
        .pipe(
          tap((state: UserIntentState) => {
            this.setState(state);
          })
        );
    } else if (this.auth0Service.tokens?.current$?.value) {
      return this.intentApiService.getCurrentStateByUserIntentId().pipe(
        switchMap((value: IntentValue) => {
          const newUserIntentId = value?.value;

          if (newUserIntentId) {
            this.setUserIntentIdToLocalStorage(newUserIntentId);
            return this.intentApiService
              .getCurrentStateByUserIntentId(newUserIntentId)
              .pipe(
                tap((state: UserIntentState) => {
                  this.setState(state);
                })
              );
          } else {
            return of({});
          }
        })
      );
    } else {
      return of({});
    }
  }

  public nextStep(
    jumpToState?: OnboardingIntentStep,
    newProperties?: IntentProperties
  ): Observable<UserIntentState | unknown> {
    const userIntentId = this.getIntentIdFromLocalStorage();

    if (!userIntentId) {
      return EMPTY;
    }

    const milestoneState = this.state$.value?.milestoneState;
    const properties = this.state$.value?.properties;

    const nextStep$ = this.intentApiService
      .updateUserIntent(userIntentId, jumpToState, {
        ...properties,
        ...newProperties,
      })
      .pipe(
        tap((state: UserIntentState) => {
          this.setState(state);
        })
      );

    return this.fetchUserIntent().pipe(
      first(),
      switchMap((state: UserIntentState) => this.verifyIntentActive(state)),
      mergeMap((newState: UserIntentState) =>
        iif(
          () =>
            newState.milestoneState === OnboardingIntentStep.Welcome ||
            newState.milestoneState === milestoneState,
          nextStep$,
          // SYNC MILESTONE/STEP IF BE IS AHEAD OF FE
          this.intentApiService
            .updateUserIntent(userIntentId, newState.milestoneState)
            .pipe(
              tap((state: UserIntentState) => {
                this.setState(state);
              })
            )
        )
      )
    );
  }

  public cancelIntent(): Observable<unknown> {
    const userIntentId = this.getIntentIdFromLocalStorage();

    if (!userIntentId) {
      // This will prevent error when intent is canceled in a different tab
      this.clear();
      return EMPTY;
    }

    return this.intentApiService
      .getCurrentStateByUserIntentId(userIntentId)
      .pipe(
        switchMap((state: UserIntentState) => {
          // You cannot delete intent that has been canceled or finished.
          const { isCanceled, isFinished } = state;
          if (isCanceled || isFinished) {
            this.clear();
            return of(null);
          }
          return this.intentApiService.deleteUserIntent(userIntentId);
        }),
        tap(() => {
          this.clear();
        })
      );
  }

  public isUserStillInIntent(state: UserIntentState): boolean {
    return this.isUserInIntent(state) && !!this.getIntentIdFromLocalStorage();
  }

  public isUserInIntent(state: UserIntentState): boolean {
    if (state?.milestoneState && !state.isCanceled && !state.isFinished) {
      return true;
    } else {
      this.clear();
      return false;
    }
  }

  public clear(): void {
    this.state$.next(undefined);
    this.removeIntentIdFromLocalStorage();
  }

  public verifyIntentActive(
    state: UserIntentState
  ): Observable<UserIntentState> {
    if (!state.isCanceled && !state.isFinished) {
      // Intent is still active
      return of(state);
    }

    this.clear();

    return this.auth0Service.token$.pipe(
      first(),
      extractAccount(),
      switchMap((account: Auth0Account) => {
        if (!account['http://schemas.nomnio.com/2017/5/customer']) {
          return this.auth0Service.refresh();
        }
        return of(null);
      }),
      tap(() => this.router.navigate(['/dashboard'])),
      map(() => state)
    );
  }

  private getIntentIdFromLocalStorage(): string | null {
    return localStorage.getItem(LocalStorageKey.USER_INTENT);
  }

  private setUserIntentIdToLocalStorage(userIntentId: string): void {
    localStorage.setItem(LocalStorageKey.USER_INTENT, userIntentId);
    // eslint-disable-next-line @typescript-eslint/naming-convention
    Sentry.setContext('INTENT', { 'User intent id': userIntentId });
  }

  private removeIntentIdFromLocalStorage(): void {
    localStorage.removeItem(LocalStorageKey.USER_INTENT);
    Sentry.setContext('INTENT', {});
  }

  private setState(state: UserIntentState): void {
    if (state?.milestoneState) {
      this.state$.next({ ...this.state$.value, ...state });
      if (!this.inIntent) {
        this.stayInIntent();
        this.preventIllegalState();
        this.inIntent = true;
      }
    } else {
      this.state$.next(undefined);
    }
  }

  private cancelLegacyIntents(): void {
    this.state$.subscribe((state) => {
      if (
        state?.intentId === environment.intents.claimKit ||
        state?.intentId === environment.intents.claimKitInApp
      ) {
        this.cancelIntent().subscribe(() => {
          this.injector.get(Router).navigate(['/']);
        });
      }
    });
  }

  private stayInIntent(): void {
    // Any route that isn't milestone of intent steps
    const EXCLUDED_ROUTES = [
      '/onboarding/download',
      '/onboarding/preferences',
      '/onboarding/sex',
      '/onboarding/measurements',
      '/onboarding/ethnicities',
      '/onboarding/language',
      '/onboarding/already-activated',
      '/accounts/reset',
      '/accounts/logout',
      '/accounts/clear',
      '/nipt/nipt-expired',
    ];
    let tempCurrentRoute: string;

    this.router.events
      .pipe(
        takeWhile(() => this.isUserStillInIntent(this.state$.value)),
        filter((event) => event instanceof NavigationStart)
      )
      .subscribe((event: NavigationStart) => {
        // Prevent user to go outside of the intent flow
        if (
          event.url !== tempCurrentRoute &&
          !EXCLUDED_ROUTES.find((url) => event.url.includes(url))
        ) {
          tempCurrentRoute = event.url;
          this.intentStrategyService.context.onAppInit(
            this.state$.value.milestoneState
          );
        }
      });
  }

  private preventIllegalState() {
    const navigationStart$: Observable<Event> = this.router.events.pipe(
      filter((event) => event instanceof NavigationStart),
      distinctUntilChanged()
    );

    const extractAccount$ = this.auth0Service.token$.pipe(
      first(),
      extractAccount()
    );

    combineLatest([extractAccount$, navigationStart$])
      .pipe(
        takeWhile(() => this.isUserInIntent(this.state$.value)),
        tap(([account]) => {
          // Prevent impossible intent url combination
          if (
            this.state$.value.milestoneState === OnboardingIntentStep.Login &&
            account?.email_verified
          ) {
            // We will update intent from the server
            // It's using debounce to avoid too many parallel API calls
            this.debounceIllegalSateCall.next();
          }
        })
      )
      .subscribe();
  }
}
